import { ShippoApi } from '../util/api';
import { OWN } from '../util/constants';
import { StatusCode } from '../util/enums';
import { storableError } from '../util/errors';
import { isArrayLength } from '../util/genericHelpers';
import * as log from '../util/log';
import { shippoDebugLog } from '../util/shippoLabelDebug';
import {
  filterSupportedShippingCarriers,
  isCanadaPostCarrier,
} from '../util/shippingCarrierSupport';
import { buildNoShippingOptionsError, normalizeShippingRateError } from '../util/shippingRateError';
import { hasValidShippingDetails } from '../util/userDataExtractor';
import { fetchCurrentUser } from './user.duck';

const CARRIERS_CACHE_TTL_MS = 5 * 60 * 1000;
const USD_CURRENCY = 'USD';
const carriersRequestCache = new Map();
const carriersRequestInFlight = new Map();
let latestCarriersRequestKey = null;

const toRateObjectId = rate => rate?.objectId || rate?.object_id || null;

const toNumber = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeCurrencyCode = currencyCode => {
  if (!currencyCode) {
    return null;
  }

  return String(currencyCode)
    .trim()
    .toUpperCase();
};

const normalizeAmountValue = amount => {
  if (amount === null || amount === undefined || amount === '') {
    return null;
  }

  const parsed = Number(amount);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return typeof amount === 'string' ? amount : String(amount);
};

const toAmountInSubunits = amount => {
  const parsed = Number(amount);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
};

const resolveUsdAmount = carrier => {
  const amount = normalizeAmountValue(carrier?.amount);
  const currency = normalizeCurrencyCode(carrier?.currency);
  if (currency === USD_CURRENCY && amount !== null) {
    return amount;
  }

  const amountLocal = normalizeAmountValue(carrier?.amountLocal ?? carrier?.amount_local);
  const currencyLocal = normalizeCurrencyCode(carrier?.currencyLocal ?? carrier?.currency_local);
  if (currencyLocal === USD_CURRENCY && amountLocal !== null) {
    return amountLocal;
  }

  return normalizeAmountValue(
    carrier?.amountUsd ?? carrier?.amount_usd ?? carrier?.usdAmount ?? carrier?.usd_amount
  );
};

const normalizeCarrier = carrier => {
  if (!carrier || typeof carrier !== 'object') {
    return null;
  }

  if (isCanadaPostCarrier(carrier)) {
    shippoDebugLog('CHECKOUT', 'dropping unsupported carrier rate', {
      rateId: toRateObjectId(carrier),
      provider: carrier?.provider || carrier?.carrier || carrier?.carrierName || null,
    });
    return null;
  }

  const usdAmount = resolveUsdAmount(carrier);
  const amountInSubunits = usdAmount !== null ? toAmountInSubunits(usdAmount) : null;
  if (usdAmount === null || amountInSubunits === null) {
    shippoDebugLog('CHECKOUT', 'dropping non-USD carrier rate', {
      rateId: toRateObjectId(carrier),
      currency: carrier?.currency ?? carrier?.currency_local ?? carrier?.currencyLocal ?? null,
    });
    return null;
  }

  const objectId = toRateObjectId(carrier);
  const providerImage75 = carrier?.providerImage75 ?? carrier?.provider_image_75 ?? null;
  const providerImage200 = carrier?.providerImage200 ?? carrier?.provider_image_200 ?? null;
  const carrierAccount = carrier?.carrierAccount ?? carrier?.carrier_account ?? null;
  const servicelevel = carrier?.servicelevel ?? carrier?.serviceLevel ?? null;

  return {
    ...carrier,
    objectId,
    object_id: objectId,
    amount: usdAmount,
    amountInSubunits,
    amountLocal: usdAmount,
    amount_local: usdAmount,
    currency: USD_CURRENCY,
    currencyLocal: USD_CURRENCY,
    currency_local: USD_CURRENCY,
    providerImage75,
    provider_image_75: providerImage75,
    providerImage200,
    provider_image_200: providerImage200,
    carrierAccount,
    carrier_account: carrierAccount,
    servicelevel,
  };
};

export const normalizeShippoRatesResponse = data => {
  const payload = data || {};
  const carriers = filterSupportedShippingCarriers(
    Array.isArray(payload?.carriers) ? payload.carriers.map(normalizeCarrier).filter(Boolean) : []
  );

  return {
    ...payload,
    carriers,
    parcelId: payload?.parcelId || payload?.parcel_id || null,
    isInternational: Boolean(payload?.isInternational),
  };
};

const getCartRequestKey = cart => {
  if (!Array.isArray(cart)) {
    return '';
  }

  return cart
    .map(item => (typeof item === 'string' ? item : item?.listingId || item?.id || ''))
    .filter(Boolean)
    .join(',');
};

const buildCarriersRequestKey = params => {
  const request = params || {};
  const length = toNumber(request?.defaultDimensions?.length);
  const width = toNumber(request?.defaultDimensions?.width);
  const height = toNumber(request?.defaultDimensions?.height);

  return [
    getCartRequestKey(request?.cart),
    request?.toAddressId || '',
    request?.fromAddressId || '',
    toNumber(request?.totalPrice),
    length,
    width,
    height,
    request?.currentUserId || '',
  ].join('|');
};

const summarizeShippingRequest = params => {
  const request = params || {};
  return {
    cartItemCount: Array.isArray(request?.cart) ? request.cart.length : 0,
    currentUserId: request?.currentUserId || null,
    fromAddressId: request?.fromAddressId || null,
    hasDefaultDimensions: Boolean(request?.defaultDimensions),
    mapMode: request?.mapSearch || null,
    selectedShippingMethod: request?.shippingMethod || null,
    toAddressId: request?.toAddressId || null,
    totalPrice: request?.totalPrice ?? null,
  };
};

const getCachedCarriers = requestKey => {
  if (!requestKey) {
    return null;
  }

  const existing = carriersRequestCache.get(requestKey);
  if (!existing) {
    return null;
  }

  if (Date.now() - existing.timestamp > CARRIERS_CACHE_TTL_MS) {
    carriersRequestCache.delete(requestKey);
    return null;
  }

  return existing.data;
};

const setCachedCarriers = (requestKey, data) => {
  if (!requestKey) {
    return;
  }

  carriersRequestCache.set(requestKey, {
    timestamp: Date.now(),
    data,
  });
};

// ================ Action types ================ //

export const FETCH_LISTINGS_IN_PROGRESS = 'app/shippo/FETCH_LISTINGS_IN_PROGRESS';
export const FETCH_LISTINGS_SUCCESS = 'app/shippo/FETCH_LISTINGS_SUCCESS';

export const SAVE_CONTACT_DETAILS_CLEAR = 'app/shippo/SAVE_CONTACT_DETAILS_CLEAR';

export const RESET_PASSWORD_REQUEST = 'app/shippo/RESET_PASSWORD_REQUEST';
export const RESET_PASSWORD_SUCCESS = 'app/shippo/RESET_PASSWORD_SUCCESS';
export const RESET_PASSWORD_ERROR = 'app/shippo/RESET_PASSWORD_ERROR';

export const CREATE_SHIPPO_ADDRESS_REQUEST = 'app/shippo/CREATE_SHIPPO_ADDRESS_REQUEST';
export const CREATE_SHIPPO_ADDRESS_SUCCESS = 'app/shippo/CREATE_SHIPPO_ADDRESS_SUCCESS';
export const CREATE_SHIPPO_ADDRESS_ERROR = 'app/shippo/CREATE_SHIPPO_ADDRESS_ERROR';

export const SAVE_LISTINGS_RESPONSE = 'app/shippo/SAVE_LISTINGS_RESPONSE';
export const SAVE_WEIGHT_IN_PROGRESS = 'app/shippo/SAVE_WEIGHT_IN_PROGRESS';

export const CLEAR_FORM = 'app/shippo/CLEAR_FORM';
export const FETCH_CARRIER_REQUEST = 'app/shippo/FETCH_CARRIER_REQUEST';
export const FETCH_CARRIER_SUCCESS = 'app/shippo/FETCH_CARRIER_SUCCESS';
export const FETCH_CARRIER_ERROR = 'app/shippo/FETCH_CARRIER_ERROR';
export const FETCH_IS_INTERNATIONAL_SHIPPING = 'app/shippo/FETCH_IS_INTERNATIONAL_SHIPPING';
export const MISSING_SHIPPING_DETAILS_ERROR = 'MISSING_SHIPPING_DETAILS';

// ================ Reducer ================ //

const initialState = {
  saveShippoAddressInProgress: false,
  shippoAddressSaved: false,
  saveShippoAddressError: null,
  clearForm: false,
  saveWeightInProgress: false,
  fetchListingsInProgress: false,
  listingsFetched: false,
  saveWeightInProgress: false,
  allListings: [],
  fetchCarriersInProgress: false,
  carriersData: {},
  fetchCarriersError: null,
  carriersError: null,
  isInternational: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTINGS_IN_PROGRESS:
      return {
        ...state,
        fetchListingsInProgress: true,
      };
    case FETCH_LISTINGS_SUCCESS:
      return { ...state, listingsFetched: false, fetchListingsInProgress: false };
    case CREATE_SHIPPO_ADDRESS_REQUEST:
      return {
        ...state,
        saveShippoAddressInProgress: true,
        saveShippoAddressError: null,
        shippoAddressSaved: false,
      };
    case CREATE_SHIPPO_ADDRESS_SUCCESS:
      return {
        ...state,
        saveShippoAddressInProgress: false,
        saveShippoAddressError: null,
        shippoAddressSaved: true,
      };
    case CREATE_SHIPPO_ADDRESS_ERROR:
      return {
        ...state,
        saveShippoAddressInProgress: false,
        saveShippoAddressError: payload,
      };
    case SAVE_LISTINGS_RESPONSE:
      return { ...state, allListings: payload.data };
    case CLEAR_FORM:
      return { ...state, clearForm: true };
    case SAVE_WEIGHT_IN_PROGRESS:
      return { ...state, saveWeightInProgress: true };
    case FETCH_CARRIER_REQUEST:
      return {
        ...state,
        fetchCarriersInProgress: true,
        fetchCarriersError: null,
        carriersError: null,
      };
    case FETCH_CARRIER_SUCCESS:
      return {
        ...state,
        fetchCarriersInProgress: false,
        carriersData: payload,
        fetchCarriersError: null,
        carriersError: null,
      };
    case FETCH_CARRIER_ERROR:
      return {
        ...state,
        fetchCarriersInProgress: false,
        fetchCarriersError: payload,
        carriersError: payload,
      };
    case FETCH_IS_INTERNATIONAL_SHIPPING:
      return { ...state, isInternational: payload?.isInternational };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchListingsInProgress = () => ({ type: FETCH_LISTINGS_IN_PROGRESS });
export const fetchListingsSuccess = () => ({ type: FETCH_LISTINGS_SUCCESS });

export const createShippoAddressRequest = () => ({ type: CREATE_SHIPPO_ADDRESS_REQUEST });
export const createShippoAddressSuccess = () => ({ type: CREATE_SHIPPO_ADDRESS_SUCCESS });
export const createShippoAddressError = error => ({
  type: CREATE_SHIPPO_ADDRESS_ERROR,
  payload: error,
});
export const clearForm = () => ({ type: CLEAR_FORM });
export const saveWeightInProgress = () => ({ type: SAVE_WEIGHT_IN_PROGRESS });

export const saveListingsResponse = response => ({
  type: SAVE_LISTINGS_RESPONSE,
  payload: { data: response },
});
export const fetchCarriersRequest = () => ({ type: FETCH_CARRIER_REQUEST });
export const fetchCarriersSuccess = carriers => ({
  type: FETCH_CARRIER_SUCCESS,
  payload: carriers,
});
export const fetchCarriersError = e => ({ type: FETCH_CARRIER_ERROR, payload: e });
export const fetchIsInternationalShipping = isInternational => ({
  type: FETCH_IS_INTERNATIONAL_SHIPPING,
  payload: { isInternational },
});

export const buildMissingShippingDetailsResponse = () => ({
  statusCode: StatusCode.BAD_REQUEST,
  status: 'ERROR',
  code: MISSING_SHIPPING_DETAILS_ERROR,
  message: 'Shipping details are required before requesting shipping rates.',
});
// ================ Selectors ================ //
export const selectShippoAddressStatus = state => {
  const { saveShippoAddressInProgress, saveShippoAddressError, shippoAddressSaved } = state.shippo;
  return {
    saveShippoAddressInProgress,
    saveShippoAddressError,
    shippoAddressSaved,
  };
};
export const fetchShippingCarriersSelector = state => {
  const { carriersData, fetchCarriersError, carriersError, fetchCarriersInProgress } = state.shippo;
  return {
    carriersData,
    fetchCarriersError: fetchCarriersError || carriersError,
    fetchCarriersInProgress,
  };
};
export const selectIsShippoProfileReady = state =>
  hasValidShippingDetails(state?.user?.currentUser);
// ================ Thunks ================ //

export const createShippoAddress = params => async (dispatch, getState, sdk) => {
  dispatch(createShippoAddressRequest());
  try {
    const response = Object.keys(params).length && (await ShippoApi.createAddress(params));
    if (response.statusCode === StatusCode.CREATED) {
      dispatch(fetchCurrentUser());
      dispatch(createShippoAddressSuccess());
      // onTriggerSuccess('Shipping address saved successfully.');
      return response;
    }
  } catch (error) {
    const normalizedError = storableError(error);
    const shippingError = normalizeShippingRateError(error);
    const shippoError = {
      ...normalizedError,
      data: error?.data || null,
      details: error?.data?.details || error?.details || null,
      shippingError,
    };
    dispatch(createShippoAddressError(shippingError));
    log.error(error, 'checkout-shippo-address-create-failed', {
      hasShippingDetails: Boolean(params?.shippingDetails),
      shippingPreference: params?.shippingDetails?.shippingPreference || null,
      shippingErrorCode: shippingError.code,
      technicalDetails: shippingError.technicalDetails,
    });
    return shippoError;
  }
};

export const updateExistingListings = shippingPreference => async (dispatch, getState, sdk) => {
  try {
    const {
      data: { data: listings },
    } = (await sdk.ownListings.query({})) || {};

    if (!isArrayLength(listings)) {
      console.log('No listings to update.');
      return;
    }

    // Determine the common update parameters
    const updateParams =
      shippingPreference === OWN
        ? {
            pickupEnabled: false,
            shippingEnabled: false,
            shippingPriceInSubunitsOneItem: null,
            shippingPriceInSubunitsAdditionalItems: null,
            freeShipping: true,
          }
        : {
            pickupEnabled: false,
            shippingEnabled: true,
            shippingPriceInSubunitsOneItem: null,
            shippingPriceInSubunitsAdditionalItems: null,
            freeShipping: false,
          };

    // Use Promise.all to run all updates in parallel
    await Promise.all(
      listings.map(listing =>
        sdk.ownListings
          .update({
            id: listing.id,
            publicData: updateParams,
          })
          .then(res => console.log(res, `Updated listing: ${listing?.id?.uuid}`))
      )
    );
  } catch (error) {
    console.error('Error updating listings:', error);
  }
};

export const fetchShippingCarriers = (params, options = {}) => async (dispatch, getState, sdk) => {
  const requestParams = params || {};
  const { forceRefresh = false } = options || {};
  const requestKey = buildCarriersRequestKey(requestParams);
  const cachedResponse = forceRefresh ? null : getCachedCarriers(requestKey);
  if (cachedResponse) {
    dispatch(fetchCarriersSuccess(cachedResponse));
    dispatch(fetchIsInternationalShipping(cachedResponse?.isInternational));
    return {
      statusCode: StatusCode.SUCCESS,
      data: cachedResponse,
      cached: true,
    };
  }

  if (requestKey && carriersRequestInFlight.has(requestKey)) {
    return carriersRequestInFlight.get(requestKey);
  }

  dispatch(fetchCarriersRequest());
  latestCarriersRequestKey = requestKey;
  const currentUser = getState()?.user?.currentUser;
  const hasDestinationAddressId = Boolean(requestParams?.toAddressId);
  // Keep existing profile validation for settings pages, but allow checkout
  // requests that pass an explicit destination id. The backend resolves the seller's
  // current protected address when its public Shippo id is absent or stale.
  if (!hasDestinationAddressId && !hasValidShippingDetails(currentUser)) {
    const rawError = buildMissingShippingDetailsResponse();
    const errorResponse = normalizeShippingRateError(rawError);
    dispatch(fetchCarriersError(errorResponse));
    log.warn(
      new Error('Shipping carrier request was attempted without a valid address context'),
      'checkout-shipping-carriers-missing-address',
      {
        ...summarizeShippingRequest(requestParams),
        hasProfileShippingDetails: hasValidShippingDetails(currentUser),
      }
    );
    return { ...rawError, shippingError: errorResponse };
  }

  const requestPromise = (async () => {
    try {
      const response = await ShippoApi.getCarriers(requestParams);
      const normalizedData = normalizeShippoRatesResponse(response?.data);
      const isLatestRequest = !requestKey || latestCarriersRequestKey === requestKey;

      if (response.statusCode === StatusCode.SUCCESS) {
        const hasRates = (normalizedData?.carriers || []).length > 0;
        if (hasRates) {
          setCachedCarriers(requestKey, normalizedData);
          if (isLatestRequest) {
            dispatch(fetchCarriersSuccess(normalizedData));
            dispatch(fetchIsInternationalShipping(normalizedData?.isInternational));
          }
        } else {
          const noOptionsError = buildNoShippingOptionsError();
          if (isLatestRequest) {
            dispatch(fetchCarriersError(noOptionsError));
          }
          log.warn(
            new Error('Shipping carrier request succeeded but returned no rates'),
            'checkout-shipping-carriers-empty',
            {
              ...summarizeShippingRequest(requestParams),
              isInternational: normalizedData?.isInternational || false,
              parcelId: normalizedData?.parcelId || null,
            }
          );
          return {
            ...response,
            data: normalizedData,
            shippingError: noOptionsError,
          };
        }
      } else {
        const normalizedError = normalizeShippingRateError(response);
        if (isLatestRequest) {
          dispatch(fetchCarriersError(normalizedError));
        }
        log.error(response, 'checkout-shipping-carriers-request-failed', {
          ...summarizeShippingRequest(requestParams),
          statusCode: response?.statusCode || response?.status || null,
          shippingErrorType: normalizedError.type,
          shippingErrorCode: normalizedError.code,
        });
        return normalizedError;
      }

      return {
        ...response,
        data: normalizedData,
      };
    } catch (error) {
      const storable = storableError(error);
      const normalizedError = normalizeShippingRateError(error);
      const isLatestRequest = !requestKey || latestCarriersRequestKey === requestKey;
      if (isLatestRequest) {
        dispatch(fetchCarriersError(normalizedError));
      }
      log.error(error, 'checkout-shipping-carriers-request-failed', {
        ...summarizeShippingRequest(requestParams),
        statusCode: storable.status || null,
        shippingErrorType: normalizedError.type,
        shippingErrorCode: normalizedError.code,
        technicalDetails: normalizedError.technicalDetails,
      });
      return normalizedError;
    } finally {
      if (requestKey) {
        carriersRequestInFlight.delete(requestKey);
      }
    }
  })();

  if (requestKey) {
    carriersRequestInFlight.set(requestKey, requestPromise);
  }

  return requestPromise;
};

export const loadData = () => {
  // Since verify email happens in separate tab, current user's data might be updated
  return fetchCurrentUser();
};
